/** @jsx jsx */
import { jsx } from '@emotion/react'
import {
    AmpChartbeat,
    AmpGtm,
    AmpGtmVideoFallback,
    AmpIpsos,
    AmpNielsen,
    colors,
    FeatureToggle,
    LinkContext,
    MastHeadLogo,
    StyledCell,
    StyledFooterLogo,
    TermsFooter,
    TheWestFooterNetworkLinks,
    TheWestHeader,
    useSectionMetaInfo,
} from '@news-mono/component-library'
import { PageProps } from '@west-australian-newspapers/page-lifecycle-provider'
import React from 'react'
import version from '../../../../thewest/src/server/version'
import { getFooterLinks } from '../footer'
import { BaseTheWestProps } from './BaseTheWest'
import { StyledAmpContent, StyledBaseGrid } from './BaseTheWest.styled'

export interface Props {
    headerAd?: (rendered?: () => void) => JSX.Element | undefined
    search?: JSX.Element
    header: JSX.Element
    primaryNav: JSX.Element
    content: JSX.Element
    footerNav: JSX.Element
    footerNetworkLinks: JSX.Element
    footerTermsLinks: JSX.Element
    footerAd?: JSX.Element
}

export const BaseTheWestAmpTemplate: React.FC<Props> = (props) => (
    <StyledBaseGrid>
        <StyledCell>
            {props.header}
            {props.primaryNav}
            <StyledAmpContent>
                <StyledCell>{props.content}</StyledCell>
            </StyledAmpContent>
        </StyledCell>
        <StyledCell>{props.footerNav}</StyledCell>
        <StyledCell>
            <StyledAmpContent>{props.footerNetworkLinks}</StyledAmpContent>
        </StyledCell>
        <div style={{ backgroundColor: colors.thewest.greyMouse }}>
            <StyledAmpContent>{props.footerTermsLinks}</StyledAmpContent>
        </div>
    </StyledBaseGrid>
)

export const BaseTheWestAmp: React.FC<BaseTheWestProps> = ({
    ads,
    config,
    product,
    pageContents,
    additionalPageProperties,
    section,
    pageType,
    location,
    onEvent,
    hostname,
}) => {
    const sectionMeta = useSectionMetaInfo()
    const footerLinks = getFooterLinks(config, sectionMeta)

    return (
        <LinkContext.Provider value={{ links: footerLinks }}>
            <BaseTheWestAmpTemplate
                header={<TheWestHeader onEvent={onEvent} />}
                primaryNav={<div />}
                content={
                    <div>
                        <AmpGtm
                            gtmId={config.ampGtmId}
                            smartOctoProperties={
                                additionalPageProperties.smartOctoProperties
                            }
                        />
                        <AmpNielsen
                            ampApid={config.nielsenAmpApid}
                            apn={config.nielsenApn}
                            product={product}
                            location={location}
                            siteName={config.nielsenSiteName}
                        />
                        <FeatureToggle
                            feature="ipsos-tracking-amp"
                            on={() => (
                                <AmpIpsos
                                    hostName={hostname}
                                    pathName={location.pathname}
                                />
                            )}
                        />
                        {pageContents}
                        <PageProps
                            pageProperties={{
                                ...additionalPageProperties,
                                section,
                                pageType,
                                codebase: {
                                    version: version.buildNumber,
                                    sha: version.sha,
                                },
                            }}
                        />
                        <FeatureToggle
                            feature="native-chartbeat"
                            on={() => (
                                <AmpChartbeat
                                    config={config}
                                    sections={
                                        additionalPageProperties
                                            ?.chartbeatProperties?.sections
                                    }
                                    authors={
                                        additionalPageProperties
                                            ?.chartbeatProperties?.authors
                                    }
                                    hostname={hostname}
                                    pathname={location.pathname}
                                />
                            )}
                        />
                        <AmpGtmVideoFallback />
                    </div>
                }
                footerNav={<div />}
                footerNetworkLinks={
                    <TheWestFooterNetworkLinks
                        networkLinks={footerLinks.networkLinks}
                        socialLinks={footerLinks.socialLinks}
                    />
                }
                footerTermsLinks={
                    <TermsFooter
                        footerCompanyLinks={footerLinks.footerCompanyLinks}
                        footerTermsLinks={footerLinks.footerTermsLinks}
                        additionalTerms={
                            <StyledFooterLogo>
                                <MastHeadLogo />
                            </StyledFooterLogo>
                        }
                    />
                }
            />
        </LinkContext.Provider>
    )
}

export default BaseTheWestAmp
